import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import heatmapModule from "highcharts/modules/heatmap";
import exportingModule from "highcharts/modules/exporting";
import accessibilityModule from "highcharts/modules/accessibility";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

heatmapModule(Highcharts);
exportingModule(Highcharts);
accessibilityModule(Highcharts);

const months = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Total"
];

const AnnualCategoryTrendsChart = ({ years, currency, selectedGlobalYear }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [categories, setCategories] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  // Sync global year
  useEffect(() => {
    setSelectedYear(selectedGlobalYear);
  }, [selectedGlobalYear]);

  // Check login
  useEffect(() => {
    if (!localStorage.getItem("userData")) history.push("/login");
  }, [history]);

  const storedUserData = localStorage.getItem("userData");
  const groupId = storedUserData ? JSON.parse(storedUserData).groupId : "";

  // Detect dark mode
  useEffect(() => {
    const checkDark = () => document.documentElement.classList.contains("dark");
    setIsDarkMode(checkDark());
    const observer = new MutationObserver(() => setIsDarkMode(checkDark()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Fetch heatmap + totals
  useEffect(() => {
    async function fetchData() {
      if (!groupId) return;
      setLoading(true);

      try {
        const categoryData = await apiService.getAnnualCategoryTrends(groupId, selectedYear);
        const newCategories = [...categoryData.categories];
        const newHeatmap = [...categoryData.data];

        const totals = await apiService.getYearlyExpense(groupId, selectedYear);
        const debitIndex = newCategories.length;
        const creditIndex = newCategories.length + 1;
        newCategories.push("Total Debit", "Total Credit");

        totals.forEach(item => {
          const month = Number(item.month) - 1;
          newHeatmap.push([month, debitIndex, Number(item.totalDebit) || 0]);
          newHeatmap.push([month, creditIndex, Number(item.totalCredit) || 0]);
        });

        const fullGrid = [];
        for (let m = 0; m < 12; m++) {
          for (let c = 0; c < newCategories.length; c++) {
            const existing = newHeatmap.find(d => d[0] === m && d[1] === c);
            fullGrid.push([m, c, existing ? Number(existing[2]) : 0]);
          }
        }

        // ✅ Add Yearly Total Column (index = 12)
        const totalColumnIndex = 12;

        for (let c = 0; c < newCategories.length; c++) {
          let yearlyTotal = 0;

          for (let m = 0; m < 12; m++) {
            const val = fullGrid.find(d => d[0] === m && d[1] === c);
            yearlyTotal += val ? Number(val[2]) : 0;
          }

          // push total column data
          fullGrid.push([totalColumnIndex, c, yearlyTotal]);
        }

        setCategories(newCategories);
        setHeatmapData(fullGrid);
      } catch (err) {
        console.error("Failed to load chart data:", err);
        setCategories([]);
        setHeatmapData([]);
      }

      setLoading(false);
    }

    fetchData();
  }, [groupId, selectedYear, apiService]);

  if (loading || heatmapData.length === 0) {
    // Skeleton loader
    return (
      <div style={{
        backgroundColor: "#fff",
        boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
        borderRadius: "0.5rem",
        padding: "1rem",
        width: "100%",
        marginTop: "1rem"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
          <div style={{
            height: "1.5rem",   // h-6
            width: "12rem",     // w-48
            backgroundColor: "#e5e7eb", // light gray
            borderRadius: "0.375rem",
            animation: "pulse 1.5s infinite"
          }}></div>
          <div style={{
            height: "1.5rem",   // h-6
            width: "6rem",      // w-24
            backgroundColor: "#e5e7eb",
            borderRadius: "0.375rem",
            animation: "pulse 1.5s infinite"
          }}></div>
        </div>
        <div style={{
          height: "16rem",      // h-64
          backgroundColor: "#f3f4f6", // light gray
          borderRadius: "0.375rem",
          animation: "pulse 1.5s infinite"
        }}></div>
      </div>
    );
  }

  const options = {
    chart: { type: "heatmap", height: 400, backgroundColor: "transparent", plotBorderWidth: 1 },
    title: { text: undefined },
    xAxis: {
      categories: months,
      labels: { style: { color: isDarkMode ? "#ddd" : "#333" } },
      title: { text: "Months", style: { color: isDarkMode ? "#aaa" : "#444" } },
    },
    yAxis: {
      categories,
      reversed: true,
      title: { text: "Expense Type", style: { color: isDarkMode ? "#aaa" : "#444" } },
      labels: { style: { color: isDarkMode ? "#ddd" : "#333" }, rotation: -30 },
    },
    colorAxis: {
      min: 0,
      stops: [
        [0, isDarkMode ? "#0f172a" : "#e8f3ff"],
        [0.1, isDarkMode ? "#264fa3" : "#a6ceff"],
        [0.2, isDarkMode ? "#2962d0" : "#85b7ff"],
        [0.4, isDarkMode ? "#3b82f6" : "#78b3ff"],
        [0.55, isDarkMode ? "#4a90ff" : "#559fff"],
        [0.7, isDarkMode ? "#4e9bff" : "#468cff"],
        [0.85, isDarkMode ? "#57b0ff" : "#1f6ed8"],
        [1, isDarkMode ? "#60a5fa" : "#1d4ed8"],
      ],
    },
    legend: { align: "right", layout: "vertical", verticalAlign: "middle", symbolHeight: 200 },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const point = this.point;
        const cat = categories[point.y];
        return `<div style="padding:6px;">
          <strong>${months[point.x]}</strong><br/>
          <strong>${cat}:</strong> ${currency} ${point.value}
        </div>`;
      }
    },
    series: [
      {
        type: "heatmap",
        borderWidth: 1,
        borderColor: isDarkMode ? "#a2d2ff" : "#a2d2ff",
        data: heatmapData,
        dataLabels: {
          enabled: true,
          color: isDarkMode ? "#fff" : "#000",
          style: { textOutline: "none" },
          formatter: function () {
            return this.point.value === 0 ? "" : this.point.value;
          }
        },
      }
    ],
    credits: { enabled: false },
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 w-full mt-4">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-base">Annual Category Trends (Heatmap)</h2>

        <select
          style={{
            padding: "6px 15px",
            borderRadius: "6px",
            color: isDarkMode ? "#fff" : "#333",
            border: "solid #ddd 1px",
            minWidth: "80px"
          }}
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* ✅ Scroll Wrapper */}
      <div style={{ width: "100%", overflowX: "auto" }}>

        {/* ✅ Min Width Container */}
        <div style={{ minWidth: "700px" }}>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>

      </div>
    </div>
  );
};

export default AnnualCategoryTrendsChart;